import React, { use, useCallback } from "react";
import { DocumentInput, DocumentType } from "../__generated__/graphql";
import { DisplayableDocument } from "../contexts/DocumentManager";
import { WorkerProfileFieldsFragment } from "../__generated__/operations/graphql";

// Documents flow through as the GET_USER worker-profile fragment shape, not the schema type.
type WorkerDocument = NonNullable<WorkerProfileFieldsFragment["documents"]>[number];
import { useMutation } from "@apollo/client/react";
import { UPDATE_WORKER_PROFILE } from "../graphql/core";
import { AuthManagerContext } from "../contexts/AuthManager";

const buildDocumentInput = (d: WorkerDocument): DocumentInput => {
    return {
        // remove assets from the document ( not present in Document Input )
        id: d.id,
        type: d.type,
        documentUrl: d.documentUrl,
        title: d.title,
    };
};

export const useWorkerDocumentManager = () => {
    const { user, refreshUser } = use(AuthManagerContext);
    const [updateWorkerProfile] = useMutation(UPDATE_WORKER_PROFILE);

    const workerProfile = user?.workerProfile;

    const saveDocuments = React.useCallback(
        async (documents: Array<DocumentInput>) => {
            await updateWorkerProfile({
                variables: {
                    changes: {
                        documents,
                    },
                },
            });
            await refreshUser?.();
        },
        [updateWorkerProfile, refreshUser],
    );

    const addWorkerDocument = useCallback(
        async (documentType: DocumentType, document: WorkerDocument) => {
            if (!workerProfile) {
                throw new Error("Worker profile must exist");
            }
            if (!document.documentUrl) {
                throw new Error("Document must have a documentUrl");
            }
            if (!document.title) {
                throw new Error("Document must have a title");
            }
            const newDocuments: Array<DocumentInput> = [
                ...(workerProfile.documents || []).map(buildDocumentInput),
                {
                    type: documentType,
                    documentUrl: document.documentUrl,
                    title: document.title,
                },
            ];
            await saveDocuments(newDocuments);
        },
        [saveDocuments, workerProfile],
    );

    const removeWorkerDocument = useCallback(
        async (removedDocument: DisplayableDocument) => {
            if (!workerProfile) {
                return;
            }
            const newDocuments: Array<DocumentInput> = (workerProfile.documents || [])
                .filter((d) => {
                    return d && d.documentUrl !== removedDocument.documentUrl;
                })
                .map(buildDocumentInput);
            await saveDocuments(newDocuments);
        },
        [saveDocuments, workerProfile],
    );

    return { addWorkerDocument, removeWorkerDocument };
};
